import { Paper, Stack, Typography } from '@mui/material'
import { BookingListResponseDto } from '@planning/app/api'
import { useTranslate } from '@tolgee/react'
import { EmptyIndicator, TitleWithCaptionCell, useTomTable } from '@tom-ui/ui'
import { useTablePreferencesConfig } from '@tom-ui/utils'
import { MaterialReactTable, MRT_ColumnDef, MRT_PaginationState } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import { FC, useEffect, useMemo, useState } from 'react'
import { VesselBookingsViewStore } from '../../Stores/VesselBookingsViewStore'

interface Props {
  store: VesselBookingsViewStore
}

export const VesselBookingTable: FC<Props> = observer(({ store }) => {
  const { t } = useTranslate()

  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })

  useEffect(() => {
    store.fetch(pagination.pageIndex, pagination.pageSize)
  }, [store, pagination.pageIndex, pagination.pageSize])

  const columns = useMemo<MRT_ColumnDef<BookingListResponseDto>[]>(
    () => [
      {
        accessorKey: 'referenceNumber',
        header: t('bookingNo', 'Booking no'),
        Cell: cell => (
          <TitleWithCaptionCell
            title={cell.row.original.referenceNumber}
            caption={cell.row.original.shippingLine}
          />
        ),
      },
      {
        accessorKey: 'carrier',
        header: t('carrier', 'Carrier'),
        Cell: cell => (
          <TitleWithCaptionCell
            title={cell.row.original.vesselName}
            caption={
              cell.row.original.tripIds.length > 0
                ? cell.row.original.tripIds.join(', ')
                : EmptyIndicator
            }
          />
        ),
      },
      {
        accessorKey: 'pod1',
        header: `${t('pod-1', 'POD-1')}/${t('pod-2', 'POD-2')}`,
        Cell: cell => (
          <Stack height={'100%'} alignItems={'start'} justifyContent={'start'}>
            <Typography variant='caption'>
              {[cell.row.original.pod1, cell.row.original.pod2].filter(p => p).join('/')}
            </Typography>
          </Stack>
        ),
      },
      {
        header: t('archetype', 'Archetype'),
        Cell: cell => (
          <Typography variant='caption'>{cell.row.original.archetypes.join(', ')}</Typography>
        ),
      },
      {
        header: t('expected', 'Expected'),
        accessorKey: 'expected',
      },
      {
        header: t('onTerminal', 'On terminal'),
        accessorKey: 'onTerminal',
      },
      {
        header: t('departed', 'Departed'),
        accessorKey: 'departed',
      },
      {
        header: t('total', 'Total'),
        accessorKey: 'total',
      },
    ],
    [t],
  )

  const { savedPreferences, onPreferencesChange } = useTablePreferencesConfig(
    'planning/vessel-bookings',
  )

  const table = useTomTable(
    {
      columns,
      data: store.data,
      getRowId: row => row.id.toString(),
      manualPagination: true,
      rowCount: store.total,
      onPaginationChange: setPagination,
      state: { pagination },
      // manualSorting: true,
      // onSortingChange: setSorting
      // manualfiltering: true,
      // onColumnFiltersChange: setColumnFilters
      // onGlobalFilterChange: setGlobalFilter
    },
    undefined,
    undefined,
    { columns, savedPreferences, onPreferencesChange },
  )

  return (
    <Paper elevation={1} sx={{ overflow: 'hidden' }}>
      <MaterialReactTable table={table} />
    </Paper>
  )
})
